import { useState } from "react";
import "./EinrichtungOhneMS.scss";
import { transformPayload } from "../../Constants";
import { sendToAzureFunction, getFormData, deleteToken } from "../../services/ApiService";
import { handleFormSubmit } from "../../services/FormService";
import storePhoto from "../../resources/storePhoto.png";

interface EinrichtungOhneMSProps {
  onSubmitCallback: (status: string) => void;
  tokenID: string;
  confirmedEmail: string;
  processID: number;
}

const EinrichtungOhneMS: React.FC<EinrichtungOhneMSProps> = ({ onSubmitCallback, tokenID, confirmedEmail, processID }) => {
  const [benutzername, setBenutzername] = useState("");
  const [passwort, setPasswort] = useState("");
  const [sicherheitsfrage, setSicherheitsfrage] = useState("");
  const [securePin, setSecurePin] = useState("");
  const [confirmSecurePin, setConfirmSecurePin] = useState("");
  const [sonstiges, setSonstiges] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasConsent, setHasConsent] = useState(false);

  const [benutzernameError, setBenutzernameError] = useState("");
  const [passwortError, setPasswortError] = useState("");
  const [sicherheitsfrageError, setSicherheitsfrageError] = useState("");
  const [securePinError, setSecurePinError] = useState("");
  const [confirmSecurePinError, setConfirmSecurePinError] = useState("");
  const [sonstigesError, setSonstigesError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCancel = async () => {
    try {
      await deleteToken(tokenID!);
      // After deletion, reload to trigger token validation and show error message
      window.location.reload();
    } catch (error) {
      // Optionally, show an error if deletion failed
      alert("Fehler beim Abbrechen des Vorgangs. Bitte versuchen Sie es erneut.");
    }
  };

  // Validate phone number
  const validatePhoneNumber = (value: string) => {
    const regex = /^\+?[0-9\s\-]{9,20}$/;
    if (hasConsent && !value) {
      setPhoneNumberError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (hasConsent && !regex.test(value)) {
      setPhoneNumberError("Ungültige Telefonnummer.");
      return false;
    } else {
      setPhoneNumberError("");
      return true;
    }
  };

  // Validate Secure PIN
  const validateSecurePin = (value: string) => {
    const pinRegex = /^\d{4}$/;
    if (!value.trim()) {
      setSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (!pinRegex.test(value)) {
      setSecurePinError("Bitte geben Sie eine 4-stellige PIN ein.");
      return false;
    } else if (confirmSecurePin && value !== confirmSecurePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setSecurePinError("");
      return true;
    }
  };

  // Validate Confirm Secure PIN
  const validateConfirmSecurePin = (value: string) => {
    if (!value.trim()) {
      setConfirmSecurePinError("Dieses Feld darf nicht leer sein.");
      return false;
    } else if (value !== securePin) {
      setConfirmSecurePinError("Die PINs stimmen nicht überein.");
      return false;
    } else {
      setConfirmSecurePinError("");
      return true;
    }
  };

  // Validate the form
  const validateForm = () => {
    let isValid = true;

    // Validate Benutzername
    if (!benutzername.trim()) {
      setBenutzernameError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setBenutzernameError("");
    }

    // Validate Passwort (optional but requires Sicherheitsfrage if filled)
    if (passwort.trim() && !sicherheitsfrage.trim()) {
      setSicherheitsfrageError("Sicherheitsfrage ist erforderlich, wenn ein Passwort festgelegt wird.");
      isValid = false;
    } else {
      setSicherheitsfrageError("");
    }

    // Validate Sicherheitsfrage (optional but requires Passwort if filled)
    if (sicherheitsfrage.trim() && !passwort.trim()) {
      setPasswortError("Passwort ist erforderlich, wenn eine Sicherheitsfrage festgelegt wird.");
      isValid = false;
    } else {
      setPasswortError("");
    }

    // Validate Secure PIN
    if (!validateSecurePin(securePin)) {
      isValid = false;
    }

    // Validate Confirm Secure PIN
    if (!validateConfirmSecurePin(confirmSecurePin)) {
      isValid = false;
    }

    // Validate Sonstiges
    if (!sonstiges.trim()) {
      setSonstigesError("Dieses Feld darf nicht leer sein.");
      isValid = false;
    } else {
      setSonstigesError("");
    }

    // Validate Phone Number if consent is given
    if (hasConsent && !validatePhoneNumber(phoneNumber)) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    handleFormSubmit(
      e,
      validateForm,
      getFormData,
      transformPayload,
      sendToAzureFunction,
      setIsSubmitted,
      onSubmitCallback,
      tokenID,
      confirmedEmail,
      processID
    );
  };

  return (
    <div className="email-form">
      <h2 style={{ marginTop: 0 }}>Einrichtung Notebook ohne MS Account</h2>
      <img
        src={storePhoto}
        alt="Store Photo"
        style={{ width: "100%", height: "auto", maxWidth: "100%", borderRadius: "10px", marginBottom: "20px" }}
      />

      <p style={{width:"87%", border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "5px", textAlign: "center", marginBottom: "20px" }}>
        Bitte geben Sie Ihre Daten für die Durchführung des Services ein
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Benutzername</label>
          <input
            type="text"
            value={benutzername}
            name="benutzername"
            onChange={(e) => setBenutzername(e.target.value)}
            required
          />
          {isSubmitted && benutzernameError && (
            <small className="error-message">{benutzernameError}</small>
          )}
          <small>Der Laptop benötigt einen Benutzernamen, hier kann aber drin stehen was man möchte, es muss nicht zwingend der Name sein</small>
        </div>

        <div className="form-group">
          <label>Sicherheitsfrage (Optional)</label>
          <input
            type="text"
            value={sicherheitsfrage}
            name="sicherheitsfrage"
            onChange={(e) => setSicherheitsfrage(e.target.value)}
          />
          {isSubmitted && sicherheitsfrageError && (
            <small className="error-message">{sicherheitsfrageError}</small>
          )}
          <small>Erforderlich, wenn ein Passwort festgelegt wird</small>
        </div>

        <div className="form-group">
          <label>Passwort (Optional)</label>
          <input
            type="password"
            value={passwort}
            name="password"
            onChange={(e) => setPasswort(e.target.value)}
          />
          {isSubmitted && passwortError && (
            <small className="error-message">{passwortError}</small>
          )}
          <small>Falls der Kunde auf ein Passwort besteht, Sicherheitsfragen sind zwingend von Microsoft falls man sein Passwort vergisst</small>
        </div>

        {/* Secure PIN Fields */}
        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        <div className="form-group">
          <label>Neue PIN</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={securePin}
            name="SecurePIN"
            onChange={(e) => {
              setSecurePin(e.target.value);
              validateSecurePin(e.target.value);
              if (confirmSecurePin) validateConfirmSecurePin(confirmSecurePin);
            }}
            required
          />
          <small>Bitte legen Sie eine sichere 4-stellige PIN fest. Sie benötigen diese PIN für den Zugriff auf Ihre Falldetails und können von einem Mitarbeiter des Geschäfts zur Überprüfung abgefragt werden. Behandeln Sie die PIN vertraulich und geben Sie sie nicht an Dritte weiter.</small>
          {isSubmitted && securePinError && (
            <small className="error-message">{securePinError}</small>
          )}
        </div>

        <div className="form-group">
          <label>Neue PIN bestätigen</label>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={confirmSecurePin}
            onChange={(e) => {
              setConfirmSecurePin(e.target.value);
              validateConfirmSecurePin(e.target.value);
            }}
            required
          />
          <small>Bitte bestätigen Sie Ihre 4-stellige PIN.</small>
          {isSubmitted && confirmSecurePinError && (
            <small className="error-message">{confirmSecurePinError}</small>
          )}
        </div>

        <br />
        <div style={{ borderTop: "1px solid lightgrey" }}></div>
        <br />

        <div className="form-group">
          <label>Sonstiges / Kundenwunsch</label>
          <textarea
            value={sonstiges}
            name="sonstiges"
            onChange={(e) => setSonstiges(e.target.value)}
            rows={3}
            required
          ></textarea>
          {isSubmitted && sonstigesError && (
            <small className="error-message">{sonstigesError}</small>
          )}
        </div>

        {/* Consent Checkbox */}
        <div className="form-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={hasConsent}
              onChange={(e) => {
                setHasConsent(e.target.checked);
                if (!e.target.checked) {
                  setPhoneNumber("");
                  setPhoneNumberError("");
                }
              }}
            />
            <span className="checkmark"></span>
            Ich stimme zu, dass ich für Angelegenheiten im Zusammenhang mit dieser Anfrage kontaktiert werden darf.
          </label>
        </div>

        {/* Phone Number */}
        {hasConsent && (
          <div className="form-group">
            <label>Telefonnummer</label>
            <input
              type="tel"
              value={phoneNumber}
              name="HandyNummer"
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                validatePhoneNumber(e.target.value);
              }}
              required={hasConsent}
            />
            <small>Bitte geben Sie eine gültige Telefonnummer ein.</small>
            {isSubmitted && phoneNumberError && (
              <small className="error-message">{phoneNumberError}</small>
            )}
          </div>
        )}

        <button type="submit">Absenden</button>
        <button type="button" onClick={handleCancel} style={{ backgroundColor: "red", marginBottom: 0 }}>
          Abbrechen
        </button>
        <small style={{color: "#777"}}>Durch das Abbrechen wird der Vorgang gestoppt und Ihre Daten werden nicht weiter verwendet</small>
      </form>
    </div>
  );
};

export default EinrichtungOhneMS;